<template lang="html">
	<wizard-screen panel-class="omapi-panel__4" :completed="4" :total="5" :nav-prev="false" :nav-next="false">
		<h2 class="omapi-text-center">You're almost done!</h2>
		<core-alerts v-if="error" id="om-connect-errors" :alerts="alerts"></core-alerts>
		<div class="omapi-text-center omapi-panel-info">
			Whether you're just getting started with marketing your small business or you're an experienced Pro ready to
			grow, OptinMonster is the right tool to help you grow your lists, leads and sales without needing a coder or
			graphic designer.
		</div>
		<wizard-pricing-tray @initCheckout="openCheckoutWindow" />

		<template #after-card>
			<div class="omapi-text-center omapi-panel-disclaimer">
				By connecting, you allow OptinMonster to create and send an API key to your WordPress site, allowing a
				secure connection and seamless integration to our app.
			</div>
			<wizard-exit />
		</template>
	</wizard-screen>
</template>

<script lang="js">
import { getAppData, debug } from '@/utils'
import { mapState, mapActions, mapGetters } from 'vuex'
import { screen } from './mixins/screen'

export default {
	mixins : [ screen ],
	computed : {
		...mapState([ 'error', 'alerts' ]),
		...mapGetters([ 'userEmail', 'userFirstName', 'userLastName', 'userId', 'showProUpsells' ]),
		...mapGetters('wp', [ 'isWooActive', 'isWooConnected' ]),
		isLoading () {
			return this.$store.getters.isLoading([ 'connect', 'wizard' ])
		},
		isDisabled () {
			return this.isLoading || !this.$store.state.apiKey
		},
	},
	mounted () {
		if (this.userId) {
			return this.skip()
		}

		this.$store.commit('clearAlerts')
		window.addEventListener('message', this.listenMessage)
	},
	beforeDestroy () {
		window.removeEventListener('message', this.listenMessage)
	},
	methods : {
		...mapActions([ 'saveApiKey' ]),
		listenMessage (evt) {
			const { origin, data } = evt
			if (origin.replace(/\/$/, '') !== this.$urls.app().replace(/\/$/, '')) {
				return
			}

			if (!data || 'string' !== typeof data) {
				return
			}

			try {
				// Get passed status.
				const data = JSON.parse(evt.data)

				if (!data.omWizard) {
					throw new Error('no')
				}

				// Set with: localStorage.setItem('omWpDebug', 'purchase')
				if (debug.enabled('purchase')) {
					// eslint-disable-next-line no-console
					console.warn('10-4', data.status, { data })
				}

				// Do action based on status.
				switch (data.status) {
					case 'loaded':
						this.onCheckoutLoaded(data)
						break
					case 'registered':
						this.onCheckoutRegistered(data.apiKey)
						break
					case 'complete':
						window.location.reload()
						break
					default:
				}
			} catch (e) {
				if (debug.enabled('purchase')) {
					// eslint-disable-next-line no-console
					console.warn('error', { e, origin, data, evt })
				}
			}
		},
		openCheckoutWindow (plan = false) {
			if (this.windowInterval) {
				return
			}

			let urlPath = 'checkout/wp-plugin/'
			let modalStyles = 'width=700,height=780,left=200,top=100,popup=1'

			if ('free' !== plan) {
				const discount = 'pro' === plan ? 'INTROPRO' : 'INTROGROWTH'
				urlPath = `checkout/add/${plan}/?discount=${discount}`
				modalStyles = 'width=1000,height=900,left=100,top=100,popup=1'
			}

			let url = this.$urls.app(urlPath)
			const partnerUrl = getAppData('partnerUrl')
			if (partnerUrl) {
				const urlObj = new URL(partnerUrl)
				urlObj.searchParams.set('urllink', url)
				url = urlObj.toString()
			}

			this.childWindow = window.open(
				url,
				'_blank',
				modalStyles,
			)

			this.windowInterval = window.setInterval(() => {
				if (this.childWindow && this.childWindow.closed) {
					window.clearInterval(this.windowInterval)
					this.windowInterval = false
				}
			}, 1000)
		},
		onCheckoutLoaded () {
			const payload = {
				omWizard  : true,
				status    : 'loaded',
				origin    : window.location.protocol + '//' + window.location.host,
				siteName  : getAppData('blogname'),
				partnerId : getAppData('partnerId'),
				user      : {
					email     : this.userEmail,
					firstName : this.userFirstName,
					lastName  : this.userLastName,
				},
				onboarding : {
					via                      : 'wordpress',
					referred_by              : getAppData('referredBy'),
					woocommerce_active       : this.isWooActive,
					woocommerce_connected    : this.isWooConnected,
					usage                    : this.onboardingUsage,
					usage_other_text         : this.onboardingUsageOtherText,
					email_list               : this.onboardingEmailList,
					email_service            : this.onboardingEmailService,
					email_service_other_text : this.onboardingEmailServiceOtherText,
					goal                     : this.onboardingGoal,
					extra_features           : this.extraFeatures,
				},
			}

			// Set with: localStorage.setItem('omWpDebug', 'purchase')
			if (debug.enabled('purchase')) {
				// eslint-disable-next-line no-console
				console.warn('ping ', { payload })
			}

			this.childWindow.postMessage(JSON.stringify(payload), this.$urls.app())
		},
		onCheckoutRegistered (apiKey) {
			const done = () => {
				this.$store.commit('loading', 'wizard')

				window.location.reload()
			}
			this.saveApiKey(apiKey)
				.then(done)
				.catch(() => {
					// TODO Handle failures here.
					// What happens with user, etc? How can they proceed?
					done()
				})
		},
		canNextPage (proceed) {
			proceed.canProceed = false
			if (!this.isLoading) {
				this.openCheckoutWindow()
			}
		},
	},
};
</script>

<style lang="scss">
.omapi-screen .omapi-panel__4 {
	.omapi-card-content {
		padding: 80px 100px;
	}
}
</style>

<style scoped lang="scss">
.omapi-screen .omapi-panel__4 {
	width: 1080px;
	margin-left: -90px;
	.omapi-wizard-forward {
		border-radius: 3px;
		font-size: 16px;
		margin: 0 0 40px;
		padding: 16px 60px;
	}
	.om-circle-loading {
		margin: 0 0 20px;
	}

	.omapi-button__large {
		margin-bottom: 0 !important;
	}

	.omapi-panel-info {
		color: #646970;
		font-size: 16px;
		font-weight: 400;
		line-height: 150%;
	}

	.omapi-panel-disclaimer {
		color: #646970;
		font-size: 14px;
		font-weight: 400;
		line-height: 130%;
		margin-top: 40px;
	}

	.omapi-wizard-exit {
		margin-top: 48px;
	}
}
</style>
